import { AlertTriangle, CheckCircle2, Info, X, XCircle } from 'lucide-react'
import { cn } from '@/internationalization/cn'
import { useToastStore, type Toast as ToastModel } from '@/stores/toast-store'
import { useT } from '@/internationalization/i18n'
import { IconButton } from './IconButton'

const ICONS = {
  success: CheckCircle2,
  error: XCircle,
  warning: AlertTriangle,
  info: Info,
} as const

const TONES = {
  success: 'text-success',
  error: 'text-danger',
  warning: 'text-warning',
  info: 'text-info',
} as const

function ToastCard({ toast }: { toast: ToastModel }) {
  const t = useT()
  const dismiss = useToastStore((s) => s.dismiss)
  const Icon = ICONS[toast.tone]
  return (
    <div
      // 'assertive' for errors so a screen reader interrupts; 'polite' otherwise.
      role={toast.tone === 'error' ? 'alert' : 'status'}
      className="pointer-events-auto flex w-full animate-slide-in-bottom items-start gap-3 rounded-md border
        border-border bg-surface-raised p-3 shadow-lg sm:animate-scale-in sm:w-80"
    >
      <Icon aria-hidden className={cn('mt-0.5 h-4 w-4 shrink-0', TONES[toast.tone])} />
      <div className="min-w-0 flex-1">
        <p className="text-base font-medium text-fg">{toast.title}</p>
        {toast.description && <p className="mt-0.5 text-sm text-fg-muted">{toast.description}</p>}
        {toast.action && (
          <button
            type="button"
            onClick={() => {
              toast.action?.onClick()
              dismiss(toast.id)
            }}
            className="mt-1.5 text-sm font-medium text-brand hover:underline"
          >
            {toast.action.label}
          </button>
        )}
      </div>
      <IconButton label={t('common.dismiss')} icon={<X />} size="sm" onClick={() => dismiss(toast.id)} />
    </div>
  )
}

/**
 * Mounted once, in Providers. Bottom on mobile (thumb reach, and it does not
 * cover the top bar), top-right on desktop.
 *
 * A toast is for confirmation of something the user just did. A failure the
 * user must act on belongs in an Alert on the page — a toast disappears.
 */
export function ToastViewport() {
  const toasts = useToastStore((s) => s.toasts)
  if (toasts.length === 0) return null
  return (
    <div
      className="pointer-events-none fixed inset-x-3 bottom-3 z-toast flex flex-col gap-2
        sm:inset-x-auto sm:bottom-auto sm:right-4 sm:top-4"
    >
      {toasts.map((toast) => (
        <ToastCard key={toast.id} toast={toast} />
      ))}
    </div>
  )
}
